import { useState, useRef } from 'react';
import {
  useAccountDocuments, useDeleteDocument, type AccountDocument,
} from '../../lib/api/hooks-account.js';
import { getAccessToken } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { toastError } from '../../components/ui/Toast.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { formatBytes, relativeTime } from '../../lib/format.js';

/**
 * Account paperwork: signed orders, porting forms, proof of address.
 *
 * Staff-only, upload and download both, which is a deliberate starting point
 * rather than a limitation nobody noticed. Letting a customer supply their own
 * proof of address is the obvious next step and is a DIFFERENT access model --
 * they may see what they provided and not the internal file -- so it needs a
 * column on the row before it can be a permission.
 *
 * Nothing here expires. Removal is an explicit act, which matters because proof
 * of address and identity documents are personal information under POPIA and
 * the retention decision belongs to a person, not to a cron job.
 */

const CATEGORIES = [
  'Signed order', 'Porting form', 'Letter of authority',
  'Proof of address', 'Invoice', 'Contract', 'Other',
];

export function AccountDocsTab({ tenantId }: { tenantId: string }) {
  const docs = useAccountDocuments(tenantId);
  const remove = useDeleteDocument(tenantId);
  const can = useSession((s) => s.can);
  const [adding, setAdding] = useState(false);

  const writable = can('tenant:write');

  if (docs.error) return <ErrorBanner error={docs.error} />;

  const rows = docs.data?.data ?? [];
  const available = docs.data?.storage_available ?? true;

  return (
    <>
      {/*
        Said up front rather than discovered on a failed upload. A deployment
        without storage credentials cannot accept a file, and finding that out
        after choosing one is the worst moment to learn it.
      */}
      {docs.data && !available && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 12 }}>
          <Icon name="warning" size={14} />
          <span>
            Document storage is not configured on this deployment, so uploads
            will be refused. Existing records are listed but their files cannot
            be fetched.
          </span>
        </div>
      )}

      <div className="arc-row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        {writable && available && !adding && (
          <button type="button" className="arc-btn arc-btn-primary"
            onClick={() => setAdding(true)}>
            <Icon name="plus" size={13} /> Upload document
          </button>
        )}
      </div>

      {adding && (
        <UploadForm
          tenantId={tenantId}
          maxBytes={docs.data?.max_bytes ?? 20 * 1024 * 1024}
          accepted={docs.data?.accepted ?? []}
          onCancel={() => setAdding(false)}
          onDone={() => { setAdding(false); void docs.refetch(); }}
        />
      )}

      <div className="arc-card">
        {docs.isLoading ? (
          <TableSkeleton rows={4} cols={4} />
        ) : rows.length === 0 ? (
          <Empty
            icon="invoice"
            title="No documents on file"
            hint="Signed orders, porting forms, letters of authority — the paperwork that has to be found again when a port is queried."
          />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>Document</th><th>Category</th><th>Size</th><th>Uploaded</th>
                  <th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <div style={{ fontWeight: 550 }}>{d.title}</div>
                      <div className="arc-subtle arc-mono" style={{ marginTop: 2 }}>
                        {d.original_name}
                      </div>
                      {d.description && (
                        <div className="arc-subtle" style={{ marginTop: 2 }}>{d.description}</div>
                      )}
                    </td>
                    <td>{d.category ?? <span className="arc-subtle">—</span>}</td>
                    <td className="arc-num" style={{ whiteSpace: 'nowrap' }}>{formatBytes(d.bytes)}</td>
                    <td className="arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                      {relativeTime(d.created_at)}
                      {d.uploaded_by_name && <> · {d.uploaded_by_name}</>}
                    </td>
                    <td>
                      <div className="arc-row" style={{ gap: 4 }}>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          title="Download" disabled={!available}
                          onClick={() => void download(tenantId, d)}>
                          <Icon name="external" size={12} />
                        </button>
                        {writable && (
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            title="Delete permanently" onClick={() => remove.mutate(d.id)}>
                            <Icon name="trash" size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Downloads go through fetch, not a plain link.
 *
 * The endpoint requires an Authorization header and an <a href> cannot send
 * one -- the request would arrive unauthenticated and 401. So the bytes are
 * fetched with the token, turned into an object URL, and handed to a synthetic
 * click. The URL is revoked immediately afterwards so the blob is not pinned in
 * memory for the life of the page.
 */
async function download(tenantId: string, d: AccountDocument): Promise<void> {
  const res = await fetch(`/api/v1/tenants/${tenantId}/documents/${d.id}/file`, {
    headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
  });
  if (!res.ok) return;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = d.original_name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function UploadForm({ tenantId, maxBytes, accepted, onCancel, onDone }: {
  tenantId: string;
  maxBytes: number;
  accepted: string[];
  onCancel: () => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) return;
    // Checked here as well as on the server, so a 20 MB upload is not sent
    // across the wire only to be refused on arrival.
    if (file.size > maxBytes) {
      setError(`${file.name} is ${formatBytes(file.size)}, over the ${formatBytes(maxBytes)} limit.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('title', title || file.name);
      if (category) body.append('category', category);
      if (description) body.append('description', description);
      body.append('file', file);

      // No Content-Type header: the browser sets it with the multipart
      // boundary, and naming it ourselves omits the boundary.
      const res = await fetch(`/api/v1/tenants/${tenantId}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body,
      });
      if (!res.ok) {
        const problem = await res.json().catch(() => null);
        throw new Error(problem?.detail ?? problem?.title ?? `Upload failed (${res.status})`);
      }
      setTitle(''); setCategory(''); setDescription(''); setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      onDone();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="arc-card" style={{ marginBottom: 12 }} onSubmit={submit}>
      <div className="arc-card-head"><h2 className="arc-h2">Upload document</h2></div>
      <div className="arc-card-body">
        <div className="arc-grid arc-grid-2" style={{ gap: 10 }}>
          <label className="arc-field">
            <span>Title</span>
            <input className="arc-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={file?.name ?? 'Defaults to the file name'} />
          </label>
          <label className="arc-field">
            <span>Category</span>
            <select className="arc-select" value={category}
              onChange={(e) => setCategory(e.target.value)}>
              <option value="">Uncategorised</option>
              {CATEGORIES.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
        </div>

        <label className="arc-field" style={{ marginTop: 10 }}>
          <span>Description</span>
          <input className="arc-input" value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this is, and anything the next person needs to know" />
        </label>

        <label className="arc-field" style={{ marginTop: 10 }}>
          <span>File</span>
          <input
            ref={inputRef}
            className="arc-input"
            type="file"
            {...(accepted.length ? { accept: accepted.join(',') } : {})}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        <p className="arc-subtle" style={{ marginTop: 6 }}>
          PDFs, images, plain text and Office documents, up to {formatBytes(maxBytes)}.
          Nothing here is deleted automatically.
        </p>

        {error && (
          <div className="arc-banner arc-banner-danger" style={{ marginTop: 10 }}>
            <Icon name="warning" size={14} /><span>{error}</span>
          </div>
        )}

        <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button type="button" className="arc-btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={!file || busy}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </form>
  );
}
